import React, { useState, useRef, useEffect } from 'react';
import { SendHorizontal, AlertCircle, Loader2 } from 'lucide-react';
import { useChatInput } from '../../contexts/ChatContext';

interface ChatInputProps {
  onSubmit: (value: string) => void;
  disabled?: boolean;
  pendingMessage?: string;
}

export const ChatInput: React.FC<ChatInputProps> = ({ onSubmit, disabled, pendingMessage }) => {
  const { inputValue, setInputValue } = useChatInput();
  const textareaRef = useRef<HTMLTextAreaElement>(null);
  const [showHint, setShowHint] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Auto-focus the textarea when component mounts
  useEffect(() => {
    if (textareaRef.current && !disabled && !pendingMessage) {
      textareaRef.current.focus();
    }
  }, [disabled, pendingMessage]);

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (inputValue.trim() && !disabled && !isSubmitting) {
      setIsSubmitting(true);
      onSubmit(inputValue.trim());
      setShowHint(false);
    } else if (!inputValue.trim() && !disabled) {
      // Show hint if the input is empty and the button is clicked
      setShowHint(true);
      // Focus the textarea
      textareaRef.current?.focus();
    }
  };

  // Auto-resize textarea as content grows
  useEffect(() => {
    const textarea = textareaRef.current;
    if (textarea) {
      textarea.style.height = 'auto';
      const newHeight = Math.max(textarea.scrollHeight, 128); // Minimum height of 128px
      textarea.style.height = `${newHeight}px`;
    }
  }, [inputValue, pendingMessage]);

  // Hide hint when user starts typing
  useEffect(() => {
    if (inputValue.trim() && showHint) {
      setShowHint(false);
    }
  }, [inputValue, showHint]);

  // Reset submitting state when disabled changes or when pendingMessage changes
  useEffect(() => {
    if (!disabled && !pendingMessage) {
      setIsSubmitting(false);
    }
  }, [disabled, pendingMessage]);

  // Determine if button should be disabled (only when disabled prop is true or when submitting)
  // We don't disable the button when input is empty - we'll just show the hint instead
  const isButtonDisabled = disabled || isSubmitting;
  
  // Determine button appearance
  const buttonClasses = isSubmitting || disabled
    ? 'bg-gray-300 dark:bg-gray-600 text-gray-500 dark:text-gray-400 cursor-not-allowed'
    : 'bg-blue-600 hover:bg-blue-700 dark:bg-blue-500 dark:hover:bg-blue-600 text-white';

  return (
    <form onSubmit={handleSubmit} className="relative">
      <div className={`relative rounded-lg border ${showHint ? 'border-amber-400 dark:border-amber-500' : 'border-gray-200 dark:border-gray-600'} focus-within:ring-2 focus-within:ring-blue-500 focus-within:border-transparent transition-all ${
        disabled ? 'bg-gray-100 dark:bg-gray-600 cursor-not-allowed' : 'bg-gray-50 dark:bg-gray-700'
      }`}>
        <textarea
          ref={textareaRef}
          value={pendingMessage || inputValue}
          onChange={(e) => !disabled && setInputValue(e.target.value)}
          placeholder="Ask about matches... (e.g., 'Give me three over 2.5 matches for today')"
          className="w-full p-4 pb-16 bg-transparent border-none focus:ring-0 resize-none min-h-[128px] text-gray-900 dark:text-white placeholder-gray-500 dark:placeholder-gray-400 transition-colors outline-none"
          disabled={disabled}
        />
        
        <div className="absolute bottom-0 left-0 right-0 p-4 flex items-center justify-between bg-inherit">
          {/* Deep Analysis button is hidden for now */}
          <div className="relative z-50">
            {/* Hidden for future release */}
          </div>

          <button
            type="button"
            onClick={handleSubmit}
            disabled={isButtonDisabled}
            className={`flex items-center justify-center px-4 py-2 rounded-lg transition-colors ${buttonClasses}`}
          >
            {isSubmitting ? (
              <>
                <Loader2 className="w-5 h-5 mr-2 animate-spin" />
                <span>Processing...</span>
              </>
            ) : (
              <>
                <SendHorizontal className="w-5 h-5 mr-2" />
                <span>Get Results</span>
              </>
            )}
          </button>
        </div>
      </div>
      
      {/* Hint message positioned below the input */}
      {showHint && (
        <div className="mt-2 p-2 bg-amber-50 dark:bg-amber-900/20 text-amber-700 dark:text-amber-300 text-sm rounded-lg flex items-center gap-2 shadow-sm">
          <AlertCircle className="w-4 h-4 flex-shrink-0" />
          <span>Please type your question to get results</span>
        </div>
      )}
      
      {/* Deep Analysis explanation text is hidden for now */}
      {/* Hidden for future release */}
    </form>
  );
};